import React from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { usePageState } from '@confluence/page-context';
import { generateSlug } from '@confluence/route-manager-utils/entry-points/url';

import { ContentRedirectComponent } from './ContentRedirectComponent';
import type {
	ContentRedirectQuery$data,
	ContentRedirectQuery$variables,
} from './__generated__/ContentRedirectQuery.graphql';

export const ContentRedirect = ({ versionOverride }: ContentRedirectProps) => {
	const [{ contentId, contentSlug, spaceKey }] = usePageState();
	const { data, loading, error } = useQueryInterop<
		ContentRedirectQuery$data,
		ContentRedirectQuery$variables
	>(
		graphql`
			query ContentRedirectQuery($contentId: ID, $versionOverride: Int) {
				content(id: $contentId, version: $versionOverride) {
					nodes {
						id
						title
						space {
							id
							homepageId
							key
							alias
						}
						history {
							createdDate
						}
					}
				}
			}
		`,
		{
			errorPolicy: 'all',
			variables: {
				contentId,
				versionOverride,
			},
			context: { single: true },
		},
	);

	if (loading) {
		return null;
	}

	// UnauthorizedError is a special case here since no specific UI are required for it.
	// In case of an UnauthorizedError the data or content are expected to be empty, and
	// ContentRedirectComponent will handle such case properly.
	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	const content = data?.content?.nodes?.[0];
	const space = content?.space;
	const title = content?.title;

	const contentWithSlug = content && {
		...content,
		slug: generateSlug(title),
		spaceKey: space?.alias || space?.key,
		createdDate: content?.history?.createdDate,
	};

	return (
		<ContentRedirectComponent
			contentSlugInUrl={contentSlug}
			contentWithSlug={contentWithSlug}
			space={space}
			spaceKeyInUrl={spaceKey}
		/>
	);
};

export type ContentRedirectProps = Pick<ContentRedirectQuery$variables, 'versionOverride'>;
